import { useFuse } from "apps/visits/core/hooks/use-fuse";
import { getAllUrlParams } from "apps/visits/core/utils/getAllUrlParams";
import { debounce, deburr } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { normalizeForSearch } from "@/core/shared/v2/common/helpers/string";
import type { BloodTest } from "../models/blood-test";
import type { BloodTestPackage } from "../models/blood-test-package";
import { useSearchState } from "../store/use-search-state";
import { viewSearchResultsEvent } from "../tracking/events";

type SearchItem = { uuid: string; name: string; alternativeNames: string; searchText: string };

const fuseOptions = {
  keys: [
    { name: "name", weight: 0.5, getFn: (item: SearchItem) => normalizeForSearch(item.name) },
    { name: "alternativeNames", weight: 0.5, getFn: (item: SearchItem) => normalizeForSearch(item.alternativeNames) },
    { name: "searchText", weight: 0.1, getFn: (item: SearchItem) => normalizeForSearch(item.searchText) },
  ],
  threshold: 0.15,
  distance: 75,
  minMatchCharLength: 3,
  ignoreLocation: true,
  useExtendedSearch: false,
  ignoreFieldNorm: true,
  shouldSort: false,
  includeMatches: true,
};

const SEARCH_LIMIT_ITEMS = 100;
export const MIN_SEARCH_TERM_LENGTH = 1;
// Fuse needs 3+ matching characters (minMatchCharLength), so abbreviations like B6 or TG are matched as whole words.
const WHOLE_WORD_MAX_TERM_LENGTH = 2;

type UseCatalogSearchProps = {
  products: BloodTest[] | BloodTestPackage[];
};

export const useCatalogSearch = ({ products }: UseCatalogSearchProps) => {
  const { searchTerm, setSearchTerm, setSearchResults, clearSearch, setIsSearching } = useSearchState();
  const queryParams = useMemo(() => getAllUrlParams(), []);
  const { search: searchFuse } = useFuse<SearchItem>({
    data: products?.map(mapToSearchable) || [],
    options: fuseOptions,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: products are used inside the debounce wrapper
  const searchDebounced = useCallback(
    debounce((term: string) => {
      const query = term.trim();
      if (query.length < MIN_SEARCH_TERM_LENGTH) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      const productsResult =
        query.length <= WHOLE_WORD_MAX_TERM_LENGTH
          ? findByWholeWord(query, products)
          : searchFuse(normalizeForSearch(query), { limit: SEARCH_LIMIT_ITEMS }).map((searchResult) =>
              findProductByUuid(searchResult.uuid, products),
            );
      setSearchResults(productsResult);
      setIsSearching(false);

      viewSearchResultsEvent({
        searchTerm: term,
        findResults: productsResult.length > 0,
        numberOfSearches: productsResult.length,
        firstResult: productsResult[0]?.name,
        secondResult: productsResult[1]?.name,
        thirdResult: productsResult[2]?.name,
      });
    }, 700),
    [products],
  );

  const search = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setIsSearching(true);
      searchDebounced(term);
    },
    [setSearchTerm, setIsSearching, searchDebounced],
  );

  useEffect(() => {
    const { product: productQueryParam } = queryParams;
    if (products && products.length > 0 && searchTerm === productQueryParam) {
      search(productQueryParam);
    }
  }, [queryParams, products, search, searchTerm]);

  const clearSearchResults = () => {
    clearSearch();
  };

  return { search, clearSearchResults };
};

function mapToSearchable(product: BloodTest | BloodTestPackage) {
  return {
    uuid: product.uuid,
    name: product.name,
    alternativeNames: alternativeNamesOf(product).join(" "),
    searchText: `${product.description} ${
      "bloodTests" in product ? product.bloodTests.map((bloodTest) => bloodTest.name).join(" ") : ""
    }`,
  };
}

// Descriptions are skipped: short words there are mostly prepositions ("na", "do"). Names are full of
// single-letter words too ("w moczu", "P/c."), so a single letter only matches alternative names (K → Potas).
function findByWholeWord(term: string, products: (BloodTest | BloodTestPackage)[]) {
  const termWords = splitIntoWords(term);
  if (!termWords.length) return [];

  return products
    .filter((product) => {
      const names =
        term.length === 1 ? alternativeNamesOf(product) : [...testNamesOf(product), ...alternativeNamesOf(product)];
      const words = new Set(names.flatMap(splitIntoWords));
      return termWords.every((word) => words.has(word));
    })
    .slice(0, SEARCH_LIMIT_ITEMS);
}

function testNamesOf(product: BloodTest | BloodTestPackage) {
  return [product.name, ...("bloodTests" in product ? product.bloodTests.map((bloodTest) => bloodTest.name) : [])];
}

function alternativeNamesOf(product: BloodTest | BloodTestPackage) {
  if (!("bloodTests" in product)) return [];

  return product.bloodTests.flatMap((bloodTest) => (bloodTest.alternativeName ? [bloodTest.alternativeName] : []));
}

function splitIntoWords(str: string) {
  return deburr(str)
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

function findProductByUuid(uuid: string, products: BloodTest[] | BloodTestPackage[]) {
  return products.find((product) => product.uuid === uuid);
}
